import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  OrderingBasketAddon,
  OrderingBasketLine,
  OrderingBasketSelection,
  OrderingBasketSnapshot
} from '@/domains/pjazza/types/models/OrderingBasket.types.ts'
import type { OrderingMenuAddonGroupItem } from '@/domains/pjazza/types/models/OrderingMenu.types.ts'
import type { OrderingOrderItemPayload } from '@/domains/pjazza/types/requests/EndpointRequest.types.ts'

import { usePjazzaAuthStore } from '@/domains/pjazza/store/use-pjazza-auth-store/usePjazzaAuthStore.ts'
import { BASKET_SCHEMA_VERSION, BASKET_TTL_MS } from '@/domains/pjazza/types/consts/Basket.consts.ts'
import { basketLineKey } from '@/domains/pjazza/utils/basketLineKey.ts'
import { basketTotal } from '@/domains/pjazza/utils/basketLineTotal.ts'
import { basketSnapshotSchema } from '@/domains/pjazza/validations/ui/basketValidation.schema.ts'

/**
 * The basket lives on the device, never on the server.
 *
 * A sitting is short and a diner may lose signal mid-menu, so keeping it
 * client-side means no server round trip per tap and no orphaned carts to
 * expire. The trade is that these prices are snapshots: the server reprices
 * on quote and on placement, and a mismatch surfaces as "the menu changed"
 * rather than a wrong charge.
 */
const emptySnapshot = (siteSlug: string): OrderingBasketSnapshot => ({
  schemaVersion: BASKET_SCHEMA_VERSION,
  siteSlug,
  idempotencyKey: crypto.randomUUID(),
  updatedAt: Date.now(),
  lines: []
})

const startsAtOne = (addon: OrderingMenuAddonGroupItem): boolean =>
  addon.isQuantityEditable && !addon.isDefaultedToItemQuantity

export const useBasketStore = defineStore('ordering-basket', () => {
  const auth = usePjazzaAuthStore()

  /** What a signed-out diner tried to add, held until they sign in. */
  const pendingSelection = ref<OrderingBasketSelection | null>(null)

  // Scoped per site: two venues open in two tabs must not share a basket.
  const activeSiteSlug = useLocalStorage<string>('twn-order-basket-site', '')

  const storageKey = computed(() => `twn-order-basket:${activeSiteSlug.value || 'none'}`)

  const snapshot = useLocalStorage<OrderingBasketSnapshot>(storageKey, () => emptySnapshot(activeSiteSlug.value), {
    // A corrupt or outdated basket must reset, never crash the menu page.
    serializer: {
      read: (raw: string): OrderingBasketSnapshot => {
        const parsed = basketSnapshotSchema.safeParse(JSON.parse(raw))

        if (!parsed.success) return emptySnapshot(activeSiteSlug.value)
        if (parsed.data.schemaVersion !== BASKET_SCHEMA_VERSION) return emptySnapshot(activeSiteSlug.value)
        if (Date.now() - parsed.data.updatedAt > BASKET_TTL_MS) return emptySnapshot(activeSiteSlug.value)

        return parsed.data
      },
      write: (value: OrderingBasketSnapshot): string => JSON.stringify(value)
    },
    // Safari private mode throws on write; an in-memory basket still works.
    onError: () => undefined
  })

  const lines = computed(() => snapshot.value.lines)

  const itemCount = computed(() => lines.value.reduce((total, line) => total + line.quantity, 0))

  const isEmpty = computed(() => lines.value.length === 0)

  const currency = computed(() => lines.value[0]?.unitPrice.currency ?? 'EUR')

  /** Display-only running total; the server's quote is what gets charged. */
  const estimatedTotal = computed(() => basketTotal(lines.value, currency.value))

  /** A changed basket is a new submission, so it never reuses the key of a checkout opened for the old one. */
  const replaceLines = (lines: OrderingBasketLine[]): void => {
    snapshot.value = { ...snapshot.value, lines, idempotencyKey: crypto.randomUUID(), updatedAt: Date.now() }
  }

  /** For a key the server refused for good. */
  const renewKey = (): void => {
    snapshot.value = { ...snapshot.value, idempotencyKey: crypto.randomUUID() }
  }

  const touch = (): void => {
    snapshot.value = { ...snapshot.value, updatedAt: Date.now() }
  }

  /** Switching venues abandons the other venue's basket rather than merging it. */
  const useSite = (siteSlug: string): void => {
    if (activeSiteSlug.value === siteSlug) return

    activeSiteSlug.value = siteSlug
  }

  /**
   * Nothing enters a basket unsigned, and no dish the venue sells in person only.
   *
   * The guard is here rather than at each call site because it is an invariant of
   * the basket now, not a rule one screen enforces: the quick +, the item sheet
   * and anything added later all come through this one door. It returns false and
   * parks the selection instead of throwing, so the caller can put a sign-in in
   * front of it and then replay exactly what was asked for.
   *
   * A dish that is not orderable returns false and is never parked (DEC-2026-083).
   */
  const add = (selection: OrderingBasketSelection): boolean => {
    if (!selection.item.isOrderable) return false

    if (!auth.isSignedIn) {
      pendingSelection.value = selection

      return false
    }

    commit(selection)

    return true
  }

  /** Replays the selection that was blocked, once there is a session. */
  const addPending = (): boolean => {
    const selection = pendingSelection.value

    pendingSelection.value = null

    return selection ? add(selection) : false
  }

  const cancelPending = (): void => {
    pendingSelection.value = null
  }

  /**
   * Each option at the count the server charges when none is sent, mirroring
   * `AddonGroupItem::startingQuantity`. An editable option that does not start at
   * the line's quantity starts at 1, so it is stored as counted. Null then always
   * means the option follows the line.
   */
  const resolveAddons = (selection: OrderingBasketSelection): OrderingBasketAddon[] => {
    const quantities = new Map(selection.addons.map(addon => [addon.id, addon.quantity]))

    return selection.addonGroups
      .flatMap(group => group.items)
      .filter(addon => quantities.has(addon.id))
      .map(addon => ({
        id: addon.id,
        name: addon.name,
        price: addon.price,
        quantity: quantities.get(addon.id) ?? (startsAtOne(addon) ? 1 : null),
        // An editable option that follows the line still stops at its own maximum.
        maxQuantity: addon.isQuantityEditable ? addon.maxQuantity : null
      }))
  }

  const commit = (selection: OrderingBasketSelection): void => {
    const addons = resolveAddons(selection)

    // A counted option is the diner's exact choice, so that selection never stacks onto another line.
    const hasCountedOption = addons.some(addon => addon.quantity !== null)
    const sharedKey = basketLineKey(selection.item.id, addons, selection.notes)
    const key = hasCountedOption ? `${sharedKey}|${crypto.randomUUID()}` : sharedKey
    const existing = hasCountedOption ? undefined : snapshot.value.lines.find(line => line.key === key)

    const lines = existing
      ? snapshot.value.lines.map(line =>
          line.key === key ? { ...line, quantity: line.quantity + selection.quantity } : line
        )
      : [
          ...snapshot.value.lines,
          {
            key,
            menuItemId: selection.item.id,
            slug: selection.item.slug,
            name: selection.item.name,
            imageUrl: selection.item.imageUrl,
            unitPrice: selection.item.price,
            quantity: selection.quantity,
            addons,
            notes: selection.notes
          } satisfies OrderingBasketLine
        ]

    replaceLines(lines)
  }

  const setQuantity = (key: string, quantity: number): void => {
    if (quantity < 1) {
      remove(key)

      return
    }

    replaceLines(snapshot.value.lines.map(line => (line.key === key ? { ...line, quantity } : line)))
  }

  const remove = (key: string): void => {
    replaceLines(snapshot.value.lines.filter(line => line.key !== key))
  }

  /**
   * Cleared only once an order is actually placed — a rejected or failed
   * placement leaves the basket intact so the diner can try again.
   */
  const clear = (): void => {
    snapshot.value = emptySnapshot(activeSiteSlug.value)
  }

  const toPayload = (): OrderingOrderItemPayload[] =>
    lines.value.map(line => ({
      menu_item_id: line.menuItemId,
      quantity: line.quantity,
      // An option that follows the item sends no quantity: the server applies the line's.
      addons: line.addons.map(addon =>
        addon.quantity === null ? { id: addon.id } : { id: addon.id, quantity: addon.quantity }
      ),
      notes: line.notes
    }))

  const idempotencyKey = computed(() => snapshot.value.idempotencyKey)

  return {
    pendingSelection,
    addPending,
    cancelPending,
    lines,
    itemCount,
    isEmpty,
    estimatedTotal,
    currency,
    idempotencyKey,
    renewKey,
    useSite,
    add,
    setQuantity,
    remove,
    clear,
    touch,
    toPayload
  }
})
